import Header from "../../../components/feature/Header";
import Footer from "../../../components/feature/Footer";
import SEO from "../../../utils/seo";
import { Helmet } from "react-helmet";

const TaylorsHillServiceArea = () => {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "Starlight Motors Mobile Detailing - Taylors Hill",
    "description": "Professional mobile car detailing service in Taylors Hill, VIC. Full interior & exterior detailing, ceramic coating, and paint correction delivered to your location.",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Taylors Hill",
      "addressRegion": "VIC",
      "postalCode": "3037",
      "addressCountry": "AU"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": -37.7167,
      "longitude": 144.7500
    },
    "telephone": "0491121950",
    "url": `${import.meta.env.VITE_SITE_URL || 'https://starlightmotors.com.au'}/service-areas/taylors-hill`,
    "priceRange": "$170-$600",
    "areaServed": {
      "@type": "City",
      "name": "Taylors Hill, VIC"
    }
  };

  const services = [
    {
      title: "Full Interior & Exterior Detail",
      price: "$250",
      originalPrice: "$280",
      features: ["Complete exterior wash & wax", "Interior deep clean", "Leather conditioning", "Window cleaning", "Tire shine"]
    },
    {
      title: "Interior Only Detail",
      price: "$180",
      originalPrice: "$210",
      features: ["Deep vacuum", "Leather treatment", "Dashboard cleaning", "Seat protection", "Interior windows"]
    },
    {
      title: "Exterior Only Detail",
      price: "$120",
      originalPrice: "$150",
      features: ["Exterior wash", "Paint protection wax", "Wheel cleaning", "Tire shine", "Trim restoration"]
    },
    {
      title: "Ceramic Coating",
      price: "from $600",
      features: ["Paint correction", "Premium coating", "2-5 year protection", "Enhanced gloss", "Easy maintenance"]
    }
  ];

  return (
    <>
      <Helmet>
        <title>Mobile Car Detailing Taylors Hill | Starlight Motors - Expert Service</title>
        <meta name="description" content="Professional mobile car detailing in Taylors Hill, Melbourne. Starlight Motors delivers premium detailing to your location. Full interior & exterior detailing, ceramic coating. Call 0491121950 for Taylors Hill's best mobile detailing." />
        <meta name="keywords" content="mobile car detailing Taylors Hill, car detailing Taylors Hill Melbourne, ceramic coating Taylors Hill, mobile auto detailing" />
        <link rel="canonical" href={`${import.meta.env.VITE_SITE_URL || 'https://starlightmotors.com.au'}/service-areas/taylors-hill`} />
        <meta property="og:title" content="Mobile Car Detailing Taylors Hill | Starlight Motors" />
        <meta property="og:description" content="Professional mobile car detailing in Taylors Hill, Melbourne. Expert service at your location." />
        <meta property="og:url" content={`${import.meta.env.VITE_SITE_URL || 'https://starlightmotors.com.au'}/service-areas/taylors-hill`} />
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
        <script>
          {`(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
})(window,document,'script','dataLayer','GTM-PTMBTSXR');`}
        </script>
      </Helmet>
      <div className="min-h-screen bg-white">
        <Header />
        <main>
          <section className="relative py-20 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
            <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
            <div className="relative max-w-7xl mx-auto px-6">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
                <strong>Mobile Car Detailing Taylors Hill</strong>
              </h1>
              <p className="text-xl md:text-2xl text-gray-200 mb-8 max-w-3xl">
                Expert mobile car detailing services in Taylors Hill. Professional results delivered to your driveway.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a href="tel:0491121950" className="inline-flex items-center justify-center px-8 py-4 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors cursor-pointer whitespace-nowrap">
                  <i className="ri-phone-line mr-2 text-xl"></i>Call 0491 121 950
                </a>
                <a href="/#contact" className="inline-flex items-center justify-center px-8 py-4 bg-white text-gray-900 font-semibold rounded-lg hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap">
                  Get Free Quote
                </a>
              </div>
            </div>
          </section>

          <section className="py-16 bg-white">
            <div className="max-w-7xl mx-auto px-6">
              <div className="grid lg:grid-cols-2 gap-12 mb-16">
                <div>
                  <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                    <strong>Professional Car Detailing in Taylors Hill</strong>
                  </h2>
                  <p className="text-lg text-gray-700 mb-6">
                    Starlight Motors is Taylors Hill's premier mobile car detailing service, bringing professional-grade equipment and premium products directly to your location. Whether you're near Taylors Hill Village Shopping Centre or in the residential areas around Gourlay Road, we provide convenient, high-quality car detailing that fits your schedule.
                  </p>
                  <p className="text-lg text-gray-700 mb-6">
                    Our comprehensive mobile car detailing service in Taylors Hill covers everything from routine maintenance washes to advanced ceramic coating applications. We pride ourselves on delivering showroom-quality results while you relax at home or work.
                  </p>
                  <div className="space-y-4">
                    <div className="flex items-start">
                      <i className="ri-star-fill text-blue-600 text-xl mr-3 flex-shrink-0 mt-1"></i>
                      <p className="text-gray-700"><strong>Trusted by Taylors Hill residents</strong> for over 3 years of exceptional mobile car detailing service</p>
                    </div>
                    <div className="flex items-start">
                      <i className="ri-star-fill text-blue-600 text-xl mr-3 flex-shrink-0 mt-1"></i>
                      <p className="text-gray-700"><strong>Premium products and equipment</strong> for professional car detailing results every time</p>
                    </div>
                    <div className="flex items-start">
                      <i className="ri-star-fill text-blue-600 text-xl mr-3 flex-shrink-0 mt-1"></i>
                      <p className="text-gray-700"><strong>Fully mobile service</strong> - we bring everything needed to your Taylors Hill location</p>
                    </div>
                  </div>
                </div>
                <div>
                  <img 
                    src="https://public.readdy.ai/ai/img_res/8ce19365b81d1dfc6b27a99466511fb1.jpg"
                    alt="Mobile car detailing in Taylors Hill"
                    title="Professional car detailing Taylors Hill - Starlight Motors"
                    className="w-full h-full object-cover object-top rounded-2xl shadow-2xl"
                  />
                </div>
              </div>

              <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8 text-center">
                <strong>Taylors Hill Car Detailing Packages</strong>
              </h3>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                {services.map((service, index) => (
                  <div key={index} className="bg-white border-2 border-gray-200 rounded-xl p-6 hover:border-blue-600 hover:shadow-lg transition-all">
                    <div className="flex justify-between items-start mb-4">
                      <h4 className="text-lg font-bold text-gray-900"><strong>{service.title}</strong></h4>
                      <div className="text-right ml-2">
                        {service.originalPrice && (
                          <div className="text-xs text-gray-400 line-through">{service.originalPrice}</div>
                        )}
                        <span className="text-blue-600 font-bold text-lg whitespace-nowrap">{service.price}</span>
                      </div>
                    </div>
                    {service.originalPrice && (
                      <div className="mb-3 text-xs font-semibold text-red-600 bg-red-50 px-2 py-1 rounded">
                        Save $30!
                      </div>
                    )}
                    <ul className="space-y-2">
                      {service.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start text-sm text-gray-600">
                          <i className="ri-check-line text-blue-600 mr-2 flex-shrink-0 mt-0.5"></i>
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>

              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-8 md:p-12 text-center">
                <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                  <strong>Complete Coverage Across Taylors Hill, VIC 3037</strong>
                </h3>
                <p className="text-lg text-gray-700 mb-6 max-w-3xl mx-auto">
                  We service all areas of Taylors Hill including residential streets near Taylors Hill Village, Gourlay Road, Calder Park Drive, and surrounding neighborhoods. Our mobile car detailing service brings professional automotive care directly to your Taylors Hill driveway, saving you time while delivering exceptional results.
                </p>
                <p className="text-lg text-gray-700 mb-8 max-w-3xl mx-auto">
                  From basic maintenance details to premium ceramic coating protection, Starlight Motors is your trusted partner for mobile car detailing in Taylors Hill. Experience the convenience of professional car care without leaving home.
                </p>
                <a href="/#contact" className="inline-flex items-center px-8 py-4 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors cursor-pointer whitespace-nowrap">
                  Book Taylors Hill Detailing
                </a>
              </div>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
};

export default TaylorsHillServiceArea;
